/*!
  Pioneer WFRIA TextInputMask v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["TextInputMask"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("TextInputMask") < 0) {
    window.__PIONEER_GLOBAL__.components.push("TextInputMask");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["disabled", "customStrings", "label", "onMaskChange", "onChange", "onBlur", "onFocus", "onTextInputMaskBlur", "onValueChange", "type"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";
import Button from "../Button/Button";
import { mergeRefs } from "../common/utilities";
import { FormContext } from "../Form/FormContext";
import LiveRegion from "../private/LiveRegion/LiveRegion";
import TextInput from "../TextInput/TextInput";
import { ThemeProviderContext } from "../ThemeProvider/ThemeProviderContext";
import extractTheme from "./extractTheme";
import STRINGS from "./strings";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const CLEAR_ANNOUNCEMENT_TIMEOUT = 500;
const AUTO_HIDE_CLEAR_ANNOUNCEMENT_TIMEOUT = 5000;
const AUTO_HIDE_TIMEOUT = 20000;
/**
 * TextInputMask is a React component which creates
 * a single-line text field and label that allows
 * users to input and edit text strings. It behaves
 * exactly as the TextInput component, but is commonly
 * used for scenarios like user password input and other
 * sensitive text input fields.
 */

const TextInputMask = /*#__PURE__*/React.forwardRef((_ref, userRef) => {
  let {
    disabled,
    customStrings,
    label,
    onMaskChange,
    onChange,
    // eslint-disable-line react/prop-types
    onBlur,
    // eslint-disable-line react/prop-types
    onFocus,
    // eslint-disable-line react/prop-types
    onTextInputMaskBlur,
    onValueChange,
    type
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const context = useContext(ThemeProviderContext) || {};
  const {
    resetDone
  } = useContext(FormContext) || {};
  const buttonType = (context === null || context === void 0 ? void 0 : context.baseTheme) === "ceo" ? "standard" : "simple";
  const [masked, setMasked] = useState(true);
  const [liveRegionStatus, setLiveRegionStatus] = useState({
    message: null,
    politeness: null,
    announcementTimeout: null
  });
  const isMasked = useRef(true);
  const automaskTimer = useRef();
  const inputRef = useRef();
  const mergedInputRef = mergeRefs([userRef, inputRef]);
  const theme = useTheme();
  const {
    cssInput,
    cssButton
  } = extractTheme({
    theme
  });

  const {
    unmaskLabelText,
    maskLabelText,
    charactersHidden,
    charactersShowing,
    hidingCharacters
  } = _objectSpread(_objectSpread({}, STRINGS), customStrings);

  const buttonLabel = masked ? unmaskLabelText : maskLabelText;

  const restartAutoMaskTimer = () => {
    clearTimeout(automaskTimer.current);

    if (!isMasked.current) {
      automaskTimer.current = setTimeout(() => {
        isMasked.current = true;
        setMasked(true);
        setLiveRegionStatus({
          announcementTimeout: AUTO_HIDE_CLEAR_ANNOUNCEMENT_TIMEOUT,
          politeness: "polite",
          message: `${label} ${hidingCharacters}`
        });
      }, AUTO_HIDE_TIMEOUT);
    }
  };

  const handleShowMaskButtonClick = e => {
    e.preventDefault();
    const newHiddenState = !isMasked.current;
    isMasked.current = newHiddenState;
    restartAutoMaskTimer();
    setMasked(newHiddenState);
    setLiveRegionStatus({
      announcementTimeout: CLEAR_ANNOUNCEMENT_TIMEOUT,
      politeness: "assertive",
      message: newHiddenState ? charactersHidden : charactersShowing
    });
    /** callback that can be used by apps to generate show/hide analytics */

    onMaskChange && onMaskChange({
      e,
      masked: newHiddenState
    });
  };

  const handleTextInputMaskBlur = e => {
    var _inputRef$current;

    const {
      relatedTarget
    } = e;

    if (!((_inputRef$current = inputRef.current) !== null && _inputRef$current !== void 0 && _inputRef$current.parentElement.contains(relatedTarget))) {
      onTextInputMaskBlur && onTextInputMaskBlur();
    }
  };

  const handleFieldBlur = e => {
    restartAutoMaskTimer();
    /* istanbul ignore next */

    onBlur && onBlur(e);
    handleTextInputMaskBlur(e);
  };

  const handleFieldFocus = e => {
    restartAutoMaskTimer();
    /* istanbul ignore next */

    onFocus && onFocus(e);
  };

  const handleChange = e => {
    restartAutoMaskTimer();
    /* istanbul ignore next */

    onChange && onChange(e);
    const {
      value,
      name,
      id,
      type
    } = e.target;
    onValueChange && onValueChange({
      e,
      value,
      name,
      id,
      type
    });
  };

  useEffect(() => {
    if (resetDone) {
      setMasked(true);
      isMasked.current = true;
      clearTimeout(automaskTimer.current);
    }
  }, [resetDone]);
  /**
   * LiveRegion rules.
   *
   * If button label is Show, then characters must be masked.
   *    - For password fields, toggle type to password e.g. <input type="password">.
   *    - Once activated, toggle the input text to show characters and live region
   *      (aria-live="assertive") should announce, "showing characters" or similar.
   *  If button label is Hide, then characters must be unmasked.
   *    - For password fields, toggle type to text e.g. <input type="text">.
   *    - Once activated toggle the input text to hide characters and live region
   *      (aria-live="assertive") should announce, "hiding characters" or similar.
   *  If 20 seconds have passed and text input mask field has no focus-in nor
   *  keystrokes while text input mask characters are shown, then hide characters,
   *  toggle control label back to "Show", and use aria-live="polite" to announce
   *  "[text input mask label] hiding characters" or similar.
   */

  return _jsxs(React.Fragment, {
    children: [_jsx(TextInput, _objectSpread({
      label: label,
      type: masked ? "password" : "text",
      ref: mergedInputRef,
      disabled: disabled,
      onBlur: handleFieldBlur,
      onFocus: handleFieldFocus,
      onChange: handleChange,
      customStrings: customStrings,
      textInputCSS: cssInput,
      rightElement: _jsx(Button, {
        buttonStyles: cssButton,
        kind: buttonType,
        label: buttonLabel,
        onClick: handleShowMaskButtonClick,
        onBlur: handleTextInputMaskBlur,
        disabled: disabled
      })
    }, otherProps)), _jsx(LiveRegion, {
      role: "status",
      politeness: liveRegionStatus.politeness,
      clearAnnouncementDelay: liveRegionStatus.announcementTimeout,
      children: liveRegionStatus.message
    })]
  });
});
TextInputMask.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Label of the TextInput. */
  label: PropTypes.string.isRequired,

  /** Unique name identifying this TextInput within a form. */
  name: PropTypes.string.isRequired,
  className: PropTypes.string,

  /**
   * Override for strings used within the component.
   *  - `unmaskLabelText`: Label of the button that unmasks the input value.
   *  - `maskLabelText`: Label of the button that masks the input value.
   *  - `charactersHidden`: Live region announcement, when button is pressed to mask characters.
   *  - `charactersShowing`: Live region announcement, when button is pressed to show characters.
   *  - `hidingCharacters`: Live region announcement, when characters are hidden after inactivity.
   */
  customStrings: PropTypes.shape({
    /**
     * Text used for the control that unmasks the input value.
     * Defaults to 'Show'
     */
    unmaskLabelText: PropTypes.string,

    /**
     * Text used for the control that masks the input value.
     * Defaults to 'Hide'
     */
    maskLabelText: PropTypes.string,

    /**
     * Live region announcement, when button is pressed to mask characters.
     * Defaults to 'Characters hidden'
     */
    charactersHidden: PropTypes.string,

    /**
     * Live region announcement, when button is pressed to show characters.
     * Defaults to 'Characters showing'
     */
    charactersShowing: PropTypes.string,

    /**
     * Live region announcement, when characters are hidden after inactivity.
     * Defaults to 'hiding characters'
     */
    hidingCharacters: PropTypes.string
  }),

  /**
   * Adds custom validator(s) to the TextInput.
   *
   * This prop takes an array of callbacks that receive the field
   * value as a parameter and should return an error message if
   * the validation failed.
   *
   * Do not use `customValidators` to check if a TextInput field is empty.
   * Use the prop `required` instead.
   */
  customValidators: PropTypes.arrayOf(PropTypes.func),

  /** Disable or enable the look of the TextInput. */
  disabled: PropTypes.bool,
  display: PropTypes.string,

  /** CSS class to apply to the HTML input field. */
  fieldControlClassName: PropTypes.string,

  /** The example/hint text for TextInput. */
  hint: PropTypes.node,

  /** Hides the label visually but retain accessible relationship with the TextInput. */
  labelHidden: PropTypes.bool,

  /**
   * Removes the default asterisk icon next to the label of a required
   * TextInput field. Useful if multiple fields are required and a
   * main fieldset legend is used to convey that they are all required.
   */
  noAsterisk: PropTypes.bool,

  /**
   * Callback handler to be executed when the Show/Hide button is pressed
   * (useful for analytics for example).
   * Arguments received are:
   *  - `event`: a synthetic event.
   *  - `masked`: a boolean flag indicating if the characters are masked or not.
   */
  onMaskChange: PropTypes.func,

  /**
   * Handler for the blur event on the component.
   * Will trigger when focus moves outside the TextInputMask component from the
   * input or Show/Hide button.
   */
  onTextInputMaskBlur: PropTypes.func,

  /**
   * Handler for the onChange event on the component.
   * The onValueChange callbach function receives an object with the signature { value }
   * where value is a string containing the current value on the TextInputMask field.
   */
  onValueChange: PropTypes.func,
  placeholder: PropTypes.string,

  /** Adds an asterisk next to the label, and if within a Form, triggers validation. */
  required: PropTypes.bool,

  /**
   * Custom spacing for the component. See the
   * "Customization -> Spacing" section.
   */
  spacing: PropTypes.oneOfType([PropTypes.number, PropTypes.shape()]),

  /**
   * Default value.
   */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
} : {};
TextInputMask.defaultProps = {
  className: "",
  fieldControlClassName: "",
  display: "block",
  spacing: {},
  customValidators: null,
  customStrings: null,
  disabled: false,
  hint: null,
  labelHidden: false,
  noAsterisk: false,
  onValueChange: null,
  placeholder: null,
  required: false,
  value: null,
  onMaskChange: null,
  onTextInputMaskBlur: null
};
TextInputMask.displayName = "TextInputMask";
export default TextInputMask;